import React, { useState, useEffect, useCallback, useRef } from "react";
import { Star, X } from "lucide-react";
import { IsaiahMascotCore } from "./IsaiahMascotCore";
import { getDialogue, LEARNING_STATE_POSES } from "./IsaiahAnimationLibrary";
import "./isaiah-animations.css";

/**
 * IsaiahLearningReactions
 * Shows Isaiah's contextual reactions during learning activities.
 * Appears at meaningful moments: correct/wrong answers, completion, star rewards.
 * Uses bust/icon variants to avoid cluttering content.
 */

// Reaction configurations
const REACTION_CONFIG = {
  correct: { pose: "encouraging", duration: 2500, sparkle: true, dialogueKey: "correct" },
  wrong: { pose: "encouraging", duration: 2500, sparkle: false, dialogueKey: "wrong" },
  completion: { pose: "celebrate", duration: 4000, sparkle: true, dialogueKey: "completion" },
  stars: { pose: "celebrate", duration: 3000, sparkle: true, dialogueKey: "stars" },
  encouragement: { pose: "encouraging", duration: 2500, sparkle: false, dialogueKey: "encouragement" },
  hint: { pose: "think", duration: 3000, sparkle: false, dialogueKey: "hint" },
  welcome: { pose: "wave", duration: 3500, sparkle: false, dialogueKey: "welcome" },
  retry: { pose: "encouraging", duration: 2500, sparkle: false, dialogueKey: "retry" },
  progress: { pose: "encouraging", duration: 3000, sparkle: true, dialogueKey: "encouragement" },
  proud: { pose: "encouraging", duration: 3000, sparkle: true, dialogueKey: "completion" },
};

// Sparkle burst component
const SparkleBurst = ({ active }) => {
  if (!active) return null;
  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden">
      {Array.from({ length: 6 }, (_, i) => (
        <div
          key={i}
          className="absolute"
          style={{
            left: `${20 + Math.random() * 60}%`,
            top: `${10 + Math.random() * 60}%`,
            animation: `isaiah-sparkle-burst 0.8s ease-out ${i * 0.1}s forwards`,
            opacity: 0,
          }}
        >
          <Star size={10} className="text-yellow-400" fill="currentColor" />
        </div>
      ))}
    </div>
  );
};

/**
 * Hook: useIsaiahReactions
 * Provides a trigger function to show Isaiah reactions in learning contexts.
 */
export const useIsaiahReactions = () => {
  const [reaction, setReaction] = useState(null);
  const timerRef = useRef(null);

  const triggerReaction = useCallback((type, customMessage) => {
    if (timerRef.current) clearTimeout(timerRef.current);

    const config = REACTION_CONFIG[type] || REACTION_CONFIG.encouragement;
    const message = customMessage || getDialogue(config.dialogueKey);

    setReaction({ type, ...config, message });

    timerRef.current = setTimeout(() => {
      setReaction(null);
    }, config.duration);
  }, []);

  const clearReaction = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    setReaction(null);
  }, []);

  useEffect(() => () => { if (timerRef.current) clearTimeout(timerRef.current); }, []);

  return { reaction, triggerReaction, clearReaction };
};

/**
 * IsaiahReactionDisplay
 * Renders the reaction overlay, positioned relative to the parent.
 * Place this inside a relative-positioned container.
 */
export const IsaiahReactionDisplay = ({
  reaction,
  position = "bottom-right",
  size = "bust",
}) => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (reaction) {
      setVisible(true);
    } else {
      setVisible(false);
    }
  }, [reaction]);

  if (!visible || !reaction) return null;

  const positionClasses = {
    "bottom-right": "bottom-2 right-2",
    "bottom-left": "bottom-2 left-2",
    "top-right": "top-2 right-2",
    "bottom-center": "bottom-2 left-1/2 -translate-x-1/2",
    "inline": "relative",
  };

  const isInline = position === "inline";

  return (
    <div
      className={`${isInline ? "inline-flex items-end gap-2" : `absolute ${positionClasses[position]} z-30`} isaiah-entrance-pop`}
      data-testid="isaiah-learning-reaction"
    >
      <div className="relative">
        <SparkleBurst active={reaction.sparkle} />
        <IsaiahMascotCore
          pose={reaction.pose}
          size={size}
          glow={reaction.sparkle}
          testId="isaiah-reaction-character"
        />
      </div>
      {reaction.message && (
        <div className="isaiah-speech-enter" data-testid="isaiah-reaction-message">
          <div className="max-w-[160px] rounded-lg bg-black/60 border border-white/10 px-2.5 py-1.5 shadow-xl">
            <p className="text-white/90 text-[10px] leading-relaxed font-semibold">{reaction.message}</p>
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * IsaiahLearningGuide
 * A persistent learning guide presence for landing pages like the Learning Forest.
 * Shows Isaiah with contextual pose and message based on what area the user is exploring.
 */
export const IsaiahLearningGuide = ({
  pose = "reading",
  message = "Let's explore the Learning Forest!",
  size = "half-body",
}) => {
  const [showMessage, setShowMessage] = useState(false);
  const [hidden, setHidden] = useState(() => {
    try { return localStorage.getItem("isaiah_hero_hidden_learning") === "1"; } catch { return false; }
  });

  useEffect(() => {
    const t = setTimeout(() => setShowMessage(true), 800);
    return () => clearTimeout(t);
  }, []);

  const hide = () => { try { localStorage.setItem("isaiah_hero_hidden_learning", "1"); } catch {} ; setHidden(true); };
  const show = () => { try { localStorage.removeItem("isaiah_hero_hidden_learning"); } catch {} ; setHidden(false); };

  if (hidden) {
    return (
      <button
        onClick={show}
        data-testid="isaiah-learning-guide-restore"
        aria-label="Show Isaiah"
        className="fixed right-0 top-[150px] z-40 flex items-center justify-center isaiah-peek-wiggle"
        style={{ width: 30, height: 52, borderTopLeftRadius: 9999, borderBottomLeftRadius: 9999,
          border: "1px solid rgba(16,185,129,0.5)", borderRight: "none", background: "rgba(16,185,129,0.85)", boxShadow: "0 4px 16px rgba(0,0,0,0.4)" }}
      >
        <span style={{ fontSize: 20 }}>🌟</span>
      </button>
    );
  }

  return (
    <div className="relative isaiah-entrance-rise" data-testid="isaiah-learning-guide">
      <button
        onClick={hide}
        data-testid="isaiah-learning-guide-hide"
        aria-label="Hide Isaiah"
        className="absolute -top-1 -left-1 z-20 w-7 h-7 rounded-full bg-black/85 border border-white/30 flex items-center justify-center hover:bg-black hover:scale-110 transition-all"
      >
        <X size={14} className="text-white/85" />
      </button>
      <div className="relative">
        <IsaiahMascotCore
          pose={pose}
          size={size}
          glow
          breathing
          testId="isaiah-learning-guide-character"
        />
      </div>
      {showMessage && message && (
        <div className="absolute right-0 top-full mt-4 md:top-auto md:-bottom-2 md:right-full md:left-auto md:mr-2 md:mt-0 isaiah-speech-enter" data-testid="isaiah-learning-guide-speech">
          <div className="relative rounded-xl rounded-tr-sm md:rounded-tr-xl md:rounded-br-sm bg-black/70 md:bg-black/50 border border-emerald-500/25 md:border-emerald-500/15 px-3 py-2 shadow-xl w-[150px] md:w-[180px] max-w-[45vw] md:max-w-[70vw]">
            <p className="text-emerald-300/80 text-[9px] font-bold mb-0.5 uppercase tracking-wider">Isaiah</p>
            <p className="text-white/80 text-[11px] leading-relaxed font-semibold">{message}</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default IsaiahReactionDisplay;
